import produce from "immer";
import * as React from "react";
import { Suspense } from "react";
import { HashRouter, NavLink, Route } from "react-router-dom";
import { isOrgOwner } from "../../libs/user";
import {
  normalizeUserContextData,
  RailsUserContext,
} from "../../libs/user_context";
import Breadcrumbs from "../core/Breadcrumbs";
import PageContainer from "../core/PageContainer";
import OrgOverview from "../org/OrgOverview";
import InviteUser from "../org/InviteUser";
import OrgOnboardData from "../admin/OrgOnboardData";
import { getOrg, getOrgOwner, normalizeOrg } from "../../libs/organization";
import GARouteListener from "../utility/GARouteListener";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import ContentDiv from "../core/TabContentDiv";
import { normalizeRecord } from "../../libs/api_utils";
import OrgTransfer from "../org/OrgTransfer";
import StrategyPlan from "../org/StrategyPlan";
import OrgDisable from "../org/OrgDisable";
import OrgPermissions from "../org/OrgPermissions";

const OrgSettings = React.lazy(() => import("../org/OrgSettings"));

interface Props {
  userContext: IUserContextData;
  org: IJsonApiResponse<IOrganizationRecord>;
  cw: IJsonApiResponse<ICwMemberRecord>;
  sponsorPage: any;
  membershipTypes: { string: number }[];
}

interface State {
  userContext: IUserContextNormalized;
  org: IOrganization;
  owner: IUser;
  sponsorPage: any;
}

class OrganizationShow extends React.Component<Props, State> {
  state: Readonly<State> = {
    userContext: normalizeUserContextData(this.props.userContext),
    org: normalizeOrg(this.props.org.data),
    owner: null,
    sponsorPage: this.props.sponsorPage,
  };

  componentDidMount = async () => {
    const owner = await getOrgOwner(this.state.org);
    this.setState({ owner: owner });
  };

  fetchOrg = async () => {
    const org = await getOrg(this.state.org.id);
    this.setState({ org });
  };

  updateOwner = (newOwner: IUser) => {
    let userContext = this.state.userContext;
    userContext.user.isOwner = false;
    newOwner.isOwner = true;
    this.setState({ userContext, owner: newOwner });
  };

  render() {
    const { org, userContext, owner, sponsorPage } = this.state;

    const user = userContext.user;
    const userIsAdmin = user.isAdmin;
    const userOwnsThisOrg = isOrgOwner(user, org);
    const canEditOrg = userIsAdmin || userOwnsThisOrg;

    const breadcrumbs = userIsAdmin
      ? [
          { label: "Organizations", route: Routes.organizations_path() },
          { label: org.name },
        ]
      : [{ label: "My Organization" }];

    return (
      <RailsUserContext.Provider value={userContext}>
        <PageContainer>
          <Breadcrumbs crumbs={breadcrumbs} />
          <HashRouter hashType="noslash">
            <div>
              <GARouteListener />
              {canEditOrg && (
                <div>
                  <ul className="nav nav-tabs nav-justified">
                    <li>
                      <NavLink exact to="/">
                        Overview
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/settings">Settings</NavLink>
                    </li>
                    {userIsAdmin && (
                      <li>
                        <NavLink to="/Permissions">Permissions</NavLink>
                      </li>   
                    )}                 
                  </ul>
                </div>
              )}

              <ContentDiv>
                <Route
                  exact
                  path="/"
                  render={() => (
                    <OrgOverview org={org} cw={this.props.cw} owner={owner} sponsorPage={sponsorPage}/>
                  )}
                />
                <Route
                  path="/settings"
                  render={() =>
                    canEditOrg ? (
                      <Suspense fallback={<div>Loading...</div>}>
                        <OrgSettings
                          org={org}
                          updateSettings={(settings: IOrganizationSettings) =>
                            this.setState(
                              produce<State>(draft => {
                                draft.org.settings = settings;
                              })
                            )
                          }
                        />
                      </Suspense>
                    ) : (
                      <p>You are not authorized to edit this organization.</p>
                    )
                  }
                />
                {userIsAdmin && (
                  <Route
                    path={"/permissions"}
                    render={({ history }) => (
                      // <InviteUser org={org} routerHistory={history} />
                      <OrgPermissions orgID={parseInt(org.id)}/>
                    )}
                  />
                )}                
                <Route
                  path={"/invite"}
                  render={({ history }) => (
                    <InviteUser org={org} routerHistory={history} />
                  )}
                />
                {userIsAdmin && (
                  <Route
                    path={"/onboarding_data"}
                    render={() => (
                      <OrgOnboardData org={org} fetchOrg={this.fetchOrg} />
                    )}
                  />
                )}
                <Route
                  path={"/strategyplan"}
                  render={({ history }) => (
                    <StrategyPlan
                      org={org}
                      routerHistory={history}
                      userIsAdmin={userIsAdmin}
                    />
                  )}
                />
                {(userIsAdmin || userOwnsThisOrg) && (
                  <Route
                    path={"/transfer_owner"}
                    render={({ history }) => (
                      <OrgTransfer
                        org={org}
                        routerHistory={history}
                        updateOwner={this.updateOwner}
                      />
                    )}
                  />
                )}
                {userIsAdmin && (
                  <Route
                    path={"/disable_organization"}
                    render={({ history }) => (
                      <OrgDisable
                        org={org}
                        currentUser={user}
                        routerHistory={history}
                      />
                    )}
                  />
                )}
              </ContentDiv>
            </div>
          </HashRouter>
        </PageContainer>
      </RailsUserContext.Provider>
    );
  }
}

export default wrapComponentForErrors(OrganizationShow);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/OrganizationShow.tsx